import React, { useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import fileDownload from 'react-file-download';
import { useParams } from 'react-router-dom';

import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import { Alert, Button, CircularProgress, Paper, Stack, Tab, Tabs, Typography } from '@mui/material';

import { CopyRatePlansModal } from '~/components/Vendors/Profile/CopyRatePlansModal';
import VendorRatePlansPromotionsList from '~/components/Vendors/Profile/VendorRatePlansPromotionsList';

import { useGetOffer } from '~/hooks/apis/offers/useGetOffer';
import {
  type OfferRatePlan,
  useCloneRatePlan,
  useGetOfferRatePlans,
  useSetDefaultRatePlan,
} from '~/hooks/apis/reservation/useOfferRatePlans';
import { useEditRatePlanFormData } from '~/hooks/apis/reservation/useRatePlanFormData';
import { useGetVendor } from '~/hooks/apis/vendors/useGetVendor';

import { getRatePlansPDF } from '~/services/PDFService';

import RatePlanDialog from './RatePlanDialog';
import RatePlansTable from './RatePlansTable';

// null = closed, 'new' = create dialog, or a rate plan id = edit dialog for that plan.
type RatePlanDialogState = null | 'new' | string;

type RatePlansSubTab = 'all' | 'active' | 'unassigned' | 'promotions';

const RATE_PLANS_SUB_TABS: Array<{ label: string; value: RatePlansSubTab }> = [
  { label: 'All Rate Plans', value: 'all' },
  { label: 'Active Rate Plans', value: 'active' },
  { label: 'Unassigned Rate Plans', value: 'unassigned' },
  { label: 'Promotions', value: 'promotions' },
];

export default function RatePlansTab() {
  const { id_offer: offerId } = useParams<{ id_offer: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const [subTab, setSubTab] = useState<RatePlansSubTab>('all');
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadFailed, setDownloadFailed] = useState(false);
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  const [ratePlanDialogState, setRatePlanDialogState] = useState<RatePlanDialogState>(null);
  // Set as soon as an Edit row is clicked; cleared once its data is ready and the dialog opens (or on error).
  const [pendingEditRatePlanId, setPendingEditRatePlanId] = useState<string | null>(null);
  const { data: offer } = useGetOffer(offerId);
  const vendorAccountId = offer?.vendor_account_id;
  const { data: vendor } = useGetVendor(vendorAccountId);
  const { data: ratePlans = [], isLoading, error, refetch } = useGetOfferRatePlans(vendorAccountId);
  const cloneRatePlanMutation = useCloneRatePlan(vendorAccountId);
  const setDefaultRatePlanMutation = useSetDefaultRatePlan(vendorAccountId);
  // Shares RatePlanDialog's query (same key) so this resolves without an extra fetch once the dialog opens.
  const {
    data: pendingRatePlanData,
    isLoading: isLoadingPendingRatePlan,
    error: pendingRatePlanError,
  } = useEditRatePlanFormData(vendorAccountId, pendingEditRatePlanId ?? undefined);

  useEffect(() => {
    if (!pendingEditRatePlanId) {
      return;
    }
    if (pendingRatePlanData) {
      setRatePlanDialogState(pendingEditRatePlanId);
      setPendingEditRatePlanId(null);
    } else if (pendingRatePlanError) {
      enqueueSnackbar('Unable to load rate plan.', { variant: 'error' });
      setPendingEditRatePlanId(null);
    }
  }, [pendingEditRatePlanId, pendingRatePlanData, pendingRatePlanError, enqueueSnackbar]);

  const ratePlansBySubTab = useMemo(
    () => ({
      all: ratePlans,
      active: ratePlans.filter((ratePlan) => ratePlan.isActive),
      unassigned: ratePlans.filter((ratePlan) => ratePlan.isUnassigned),
      // A discount only counts as a promotion once it has at least one schedule attached.
      promotions: ratePlans.filter((ratePlan) =>
        (Array.isArray(ratePlan.discounts) ? ratePlan.discounts : Object.values(ratePlan.discounts ?? {})).some(
          (discount: { schedules?: Array<unknown> }) => (discount.schedules?.length ?? 0) > 0,
        ),
      ),
    }),
    [ratePlans],
  );

  const roomsByRatePlan = useMemo(
    () => Object.fromEntries(ratePlans.map((ratePlan) => [ratePlan.id, ratePlan.rooms])),
    [ratePlans],
  );

  function handleClone(ratePlan: OfferRatePlan) {
    cloneRatePlanMutation.mutate(ratePlan.id, {
      onSuccess: () => enqueueSnackbar(`Cloned "${ratePlan.name}"`, { variant: 'success' }),
      onError: (mutationError: Error) =>
        enqueueSnackbar(`Error cloning rate plan: ${mutationError.message}`, { variant: 'error' }),
    });
  }

  function handleSetDefault(ratePlan: OfferRatePlan) {
    if (ratePlan.default_plan) {
      return;
    }
    setDefaultRatePlanMutation.mutate(ratePlan.id, {
      onSuccess: () => enqueueSnackbar(`Set "${ratePlan.name}" as default`, { variant: 'success' }),
      onError: (mutationError: Error) =>
        enqueueSnackbar(`Error setting default rate plan: ${mutationError.message}`, { variant: 'error' }),
    });
  }

  function handleEdit(ratePlan: OfferRatePlan) {
    setPendingEditRatePlanId(ratePlan.id);
  }

  const visibleRatePlans = subTab === 'promotions' ? ratePlansBySubTab.all : ratePlansBySubTab[subTab];

  function handleCopyModalClose(isSaved = false) {
    setIsCopyModalOpen(false);
    if (isSaved) {
      refetch();
    }
  }

  // Downloads every rate plan for the vendor, not just the currently visible tab.
  async function handleDownload() {
    setIsDownloading(true);
    setDownloadFailed(false);
    try {
      const pdf = await getRatePlansPDF({
        list: ratePlans.map((ratePlan) => ({
          name: ratePlan.name,
          type: ratePlan.rate_type,
          comm_or_markup: ratePlan.rate_type === 'net' ? ratePlan.markup : ratePlan.commission,
        })),
        propertyName: vendor?.name || '',
      });
      fileDownload(pdf, `LuxuryEscapes-Rate-Plans-${vendorAccountId}.pdf`);
    } catch (downloadError) {
      enqueueSnackbar(`Error downloading rate plans: ${downloadError.message}`, { variant: 'error' });
      setDownloadFailed(true);
    } finally {
      setIsDownloading(false);
    }
  }

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack
        direction="row"
        flexWrap="wrap"
        useFlexGap
        justifyContent="space-between"
        alignItems="flex-start"
        gap={2}
        sx={{ mb: 3 }}
      >
        <Stack spacing={0.5} sx={{ minWidth: 0 }}>
          <Typography variant="h6">Rate Plans</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 640 }}>
            Every rate plan for this vendor, past and present. Use the tabs below to narrow to plans that are active,
            not yet sold by any offer, or currently running a promotion.
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ flexShrink: 0 }}>
          <Button variant="text" size="small" startIcon={<ContentCopyIcon />} onClick={() => setIsCopyModalOpen(true)}>
            Copy
          </Button>
          <Button
            variant="text"
            size="small"
            startIcon={isDownloading ? <CircularProgress size={16} /> : <DownloadIcon />}
            disabled={isDownloading}
            onClick={handleDownload}
          >
            {downloadFailed && !isDownloading ? 'Download Failed! Try Again' : 'Download'}
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setRatePlanDialogState('new')}>
            New rate plan
          </Button>
        </Stack>
      </Stack>

      <Tabs
        value={subTab}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        onChange={(_event, value: RatePlansSubTab) => setSubTab(value)}
      >
        {RATE_PLANS_SUB_TABS.map((tab) => (
          <Tab key={tab.value} value={tab.value} label={`${tab.label} (${ratePlansBySubTab[tab.value].length})`} />
        ))}
      </Tabs>

      {error ? (
        <Alert severity="error">Unable to load rate plans.</Alert>
      ) : subTab === 'promotions' && vendorAccountId ? (
        <VendorRatePlansPromotionsList
          ratePlans={visibleRatePlans}
          vendorId={vendorAccountId}
          roomsByRatePlan={roomsByRatePlan}
          showRoomsColumn={false}
        />
      ) : (
        <RatePlansTable
          loading={isLoading}
          ratePlans={visibleRatePlans}
          onClone={handleClone}
          onSetDefault={handleSetDefault}
          onEdit={handleEdit}
          loadingEditRatePlanId={isLoadingPendingRatePlan ? pendingEditRatePlanId : null}
        />
      )}

      <CopyRatePlansModal isOpen={isCopyModalOpen} onClose={handleCopyModalClose} vendorId={vendorAccountId} />

      {vendorAccountId && (
        <RatePlanDialog
          open={ratePlanDialogState !== null}
          onClose={() => setRatePlanDialogState(null)}
          onChanged={refetch}
          vendorId={vendorAccountId}
          ratePlanId={ratePlanDialogState !== 'new' ? (ratePlanDialogState ?? undefined) : undefined}
        />
      )}
    </Paper>
  );
}
